import { useSession } from "next-auth/react";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import Link from "next/link";
import Head from "next/head";
import SocialButton from "@/components/common/SocialButton";

export default function Login() {
  const { data: session, status } = useSession();
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { callbackUrl, error: urlError } = router.query;

  useEffect(() => {
    if (urlError) {
      setError(
        urlError === "undefined" ? "Invalid email or password" : urlError,
      );
    }
  }, [urlError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const targetUrl = callbackUrl || "/dashboard";
      const result = await signIn("credentials", {
        redirect: false,
        email,
        password,
        callbackUrl: targetUrl,
      });

      if (result?.error) {
        setError("Invalid email or password. Please try again.");
      } else if (result?.ok) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        router.push(targetUrl);
      } else {
        setError("Login failed. Please try again.");
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>Login | L.I.S.T Learning Pathways</title>
      </Head>
      <div className="min-h-screen bg-white flex flex-col md:flex-row overflow-hidden">
        {/* Left Side: Visual Branding (Hidden on Mobile) */}
        <div className="hidden md:flex md:w-[55%] relative overflow-hidden bg-brand-dark">
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[20000ms] hover:scale-110 opacity-70"
            style={{ backgroundImage: 'url("/list-hero/3.jpg")' }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-dark/80 via-transparent to-transparent" />

          <div className="relative z-10 w-full h-full flex flex-col justify-between p-16">
            <Link href="/" className="group">
              <span className="text-4xl font-black text-white tracking-tighter">
                L.I.S.T<span className="text-brand-primary">.</span>
              </span>
              <p className="text-gray-300 text-sm font-bold tracking-widest uppercase mt-1">Learning Pathways</p>
            </Link>

            <div className="max-w-xl">
              <div className="w-20 h-1 bg-brand-primary mb-8" />
              <h1 className="text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-8">
                Master the Art of <br />
                <span className="text-brand-primary text-white">Strategic Leadership.</span>
              </h1>
              <blockquote className="border-l-4 border-brand-primary/30 pl-8 italic text-2xl text-gray-200 font-medium">
                "Management is about arranging and telling. Management is about arranging and telling."
              </blockquote>
            </div>

            <div className="flex gap-8 text-white/30 font-bold text-sm tracking-widest uppercase">
              <span>Learning</span>
              <span>Inspiring</span>
              <span>Strategizing</span>
              <span>Transforming</span>
            </div>
          </div>
        </div>

        {/* Right Side: Auth Form */}
        <div className="w-full md:w-[45%] flex flex-col justify-center px-8 md:px-16 py-12 relative bg-gray-50 lg:bg-white">
          <div className="max-w-md w-full mx-auto relative z-10">
            <div className="md:hidden mb-12">
              <span className="text-3xl font-black text-brand-dark tracking-tighter">
                L.I.S.T<span className="text-brand-primary">.</span>
              </span>
            </div>

            <h2 className="text-3xl font-black text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-500 font-medium mb-10 text-lg">
              Enter your credentials to continue your journey.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-100 p-4 rounded-xl mb-8">
                <p className="text-sm text-red-600 font-bold">{error}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-300 mb-2">Email address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/10 focus:border-brand-primary transition-all font-medium shadow-sm"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-black uppercase tracking-widest text-gray-300">Password</label>
                  <Link href="/forgot-password" size="sm" className="text-xs font-black uppercase tracking-widest text-brand-primary hover:text-brand-primary/80 transition-colors">
                    Forgot?
                  </Link>
                </div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/10 focus:border-brand-primary transition-all font-medium shadow-sm"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-black uppercase tracking-widest py-4 rounded-xl shadow-lg shadow-brand-primary/10 transition-all active:scale-[0.98] flex justify-center items-center disabled:opacity-50"
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : "Sign In"}
              </button>

              <div className="relative my-10">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-100"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase font-black tracking-widest">
                  <span className="px-4 bg-gray-50 lg:bg-white text-gray-300">Or Access With</span>
                </div>
              </div>

              <SocialButton className="bg-gray-50"
                provider="google"
                text="Login via Google"
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z" fill="#EA4335" />
                  </svg>
                }
                bgColor="bg-white border border-gray-200 hover:bg-gray-50"
                textColor="text-gray-700"
                hoverBgColor="hover:bg-gray-50"
              />
            </form>

            <div className="mt-12 text-center">
              <p className="text-gray-500 font-bold">
                No account yet?{" "}
                <Link
                  href="/signup"
                  className="text-brand-primary hover:text-brand-primary/80 transition-colors ml-1"
                >
                  Apply for Access
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
